import { useEffect, useState } from "react";
import { Footer, Nav } from "@precyz/ui";
import { AuthScreen, BillingScreen, ErrorScreen, GenericScreen, LiveScreen, Locked, type Plan } from "./screens";
import { api, returnTo } from "./api";
import { routes } from "./routes";

const auth = ["signin","signin/2fa","signin/recovery","signin/enroll","signup","signup/recovery","verify-email","reset-password","accept-invite","sso","signout","session-expired","region-moved"];
const errors = new Set(["404","403","402","500","503"]);
const navBase: Array<[string,string]> = [["Live","/live"],["Posture","/posture"],["Devices","/devices"],["Alerts","/alerts"],["Reports","/reports"],["Privacy","/privacy"],["Integrations","/integrations"],["Developer","/developer"],["Billing","/billing"],["Settings","/settings/account"]];

function matches(pattern: string, path: string) {
  const p = pattern.split("/");
  const v = path.split("/");
  return p.length === v.length && p.every((part, i) => part.startsWith(":") || part === v[i]);
}

export function absentRoute(plan: Plan, path: string) {
  if (plan === "enterprise") return false;
  if (path === "/posture/targets" || path === "/reports/schedules") return true;
  if (plan === "team") return ["/org/teams","/org/scim","/org/residency","/org/keys","/org/evidence"].some(route => path === route || path.startsWith(`${route}/`));
  if (path === "/developer/clients" || path === "/billing/seats" || path === "/assistant" || path === "/audit" || path.startsWith("/audit/") || path === "/org" || path.startsWith("/org/")) return true;
  return plan === "solo" && (path === "/analysis" || path.startsWith("/analysis/"));
}

export default function App() {
  let path = location.pathname;
  if (path === "/") { history.replaceState({}, "", "/live"); path = "/live"; }
  const requested = (new URLSearchParams(location.search).get("plan") ?? "solo").toLowerCase();
  const queryPlan: Plan = (["solo","pro","team","enterprise"] as const).includes(requested as Plan) ? requested as Plan : "solo";
  const [plan, setPlan] = useState<Plan>(queryPlan);
  const [ready, setReady] = useState(false);
  const authKind = auth.find(item => path === `/${item}` || path.startsWith(`/${item}/`));

  useEffect(() => {
    if (authKind) { setReady(true); return; }
    api<{ tenant_id: string }>("/v1/users/me")
      .then(() => api<{ plan?: string; entitlements?: Record<string, unknown> }>("/v1/entitlements"))
      .then((data) => {
        const resolved = String(data.plan ?? "").toLowerCase();
        if (resolved === "solo" || resolved === "pro" || resolved === "team" || resolved === "enterprise") setPlan(resolved);
        setReady(true);
      })
      .catch((err: { status?: number }) => {
        if (err.status === 403) location.replace("/signin/2fa");
        else location.replace(returnTo(path));
      });
  }, [authKind, path]);

  if (authKind) return <AuthScreen kind={path.slice(1)}/>;
  if (!ready) return <main className="auth"><p>Watching where your connections go.</p></main>;
  const known = routes.some(route => matches(route, path));
  if (!known) return <ErrorScreen kind="404"/>;
  const section = path.split("/")[1] || "live";
  if (errors.has(section)) return <ErrorScreen kind={section}/>;
  if (absentRoute(plan, path)) return <ErrorScreen kind="404"/>;
  const locked = plan === "solo" && ["alerts","reports","integrations","developer"].includes(section) || plan === "pro" && section === "integrations";
  const navItems: Array<{label:string;href:string;locked:boolean;plan:string}> = [];
  for (const [label, href] of navBase) {
    const key = href.split("/")[1];
    navItems.push({ label, href, locked: plan === "solo" && ["alerts","reports","integrations","developer"].includes(key) || plan === "pro" && key === "integrations", plan: key === "integrations" ? "Team" : "Pro" });
  }
  if (plan !== "solo") navItems.splice(1, 0, { label: "Analysis", href: "/analysis", locked: false, plan: "Pro" });
  if (plan === "team" || plan === "enterprise") {
    navItems.splice(7, 0, { label: "Organisation", href: "/org", locked: false, plan: "Team" }, { label: "Audit", href: "/audit", locked: false, plan: "Team" });
  }
  const content = path.startsWith("/live") ? <LiveScreen/> : section === "billing" ? <BillingScreen plan={plan}/> : locked ? <Locked section={section}/> : <GenericScreen section={section} path={path}/>;
  return <div className="app-shell"><a href="#content" className="skip">Skip to content</a>
    <aside><a className="brand" href="/live">Precyz</a><Nav items={navItems} current={path}/></aside>
    <div className="app-main">
      <header className="topbar">
        <span>Personal workspace</span>
        <span>◇ Connected · {plan[0].toUpperCase() + plan.slice(1)} · <a href="/signout">Sign out</a></span>
      </header>
      <main id="content">{content}</main>
      <Footer/>
    </div>
  </div>;
}
